import { useGrowthBook } from '@growthbook/growthbook-react';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * GrowthBook feature base for the INS-1288 A/B test. Resolved per brand as
 * `show_price_without_fees_<brand>` — the naming convention every GrowthBook feature in this
 * funnel already follows.
 */
export const PRICE_WITHOUT_FEES_FEATURE = 'show_price_without_fees';

/**
 * Resolves the brand-scoped GrowthBook key.
 *
 * Exported, and shared with the non-React reader in `priceWithoutFeesExperimentSort`, so both
 * entry points produce a byte-identical key: reading the same feature under two spellings
 * would put a single user in both arms at once — a netted card above a gross-sorted list.
 *
 * @param {string} [brand] - The whitelabel brand.
 * @returns {string} The GrowthBook feature key for this brand.
 */
export const priceWithoutFeesFeatureKey = (brand) =>
  `${PRICE_WITHOUT_FEES_FEATURE}_${String(brand ?? '').toLowerCase()}`;

/**
 * The variant is the one arm that hides the charges, and only an explicit `true` selects it.
 * `undefined` (feature missing, GrowthBook unreachable, SDK not initialised, hash attribute
 * not set yet) resolves to control, so an outage can never push users into the variant nor
 * contaminate the measurement.
 *
 * @param {*} value - Whatever GrowthBook returned for the feature.
 * @returns {boolean} Whether this user is in the variant arm.
 */
export const isPriceWithoutFeesVariantValue = (value) => value === true;

/**
 * Whether this user should see the total without the convenience fee instead of the charged
 * total, for React components.
 *
 * Takes `hasDisplayTotal` and not just any truthy gate: the feature is evaluated **only** when
 * there is actually a netted figure to show. Evaluating it unconditionally would bucket users
 * whose payload carries no `displayTotal` — they cannot see any difference between the arms — and
 * every one of them would emit an `$experiment_started`, diluting the result with users the
 * experiment never reached.
 *
 * Reads the instance imperatively rather than through `useFeatureValue` so the evaluation can
 * be skipped. That costs nothing in freshness: `GrowthBookProvider` installs a renderer that
 * re-renders its whole subtree whenever `setFeatures` or `setAttributes` runs, so a component
 * that renders before the features land — or before `id` is set from the Mixpanel distinct id —
 * re-evaluates and converges on the assigned arm without a reload.
 *
 * @param {boolean} hasDisplayTotal - Whether the payload carries a display total to show.
 * @returns {boolean} Whether to render the reduced display total.
 */
export const usePriceWithoutFeesVariant = (hasDisplayTotal) => {
  const growthBookInstance = useGrowthBook();
  const { brand } = useWhitelabelEnvs() || {};

  // No brand yet means the whitelabel envs have not landed: the key would resolve to
  // `show_price_without_fees_`, a feature that does not exist, and GrowthBook would still
  // register an exposure for it. Control until we know which tenant we are.
  if (!hasDisplayTotal || !brand) return false;

  return isPriceWithoutFeesVariantValue(
    growthBookInstance?.getFeatureValue(priceWithoutFeesFeatureKey(brand)),
  );
};
